/**
 * AK-938 — «Добавить операцию» в опыте (low-fi 4·Б): тип операции, варианты,
 * дата. По одной операции на вариант (`subzone_id`), что применяли — из
 * варианта (подставлено в комментарий, можно поправить).
 *
 * Несколько вариантов = массовое действие: запросы по одному, отказы копятся,
 * ОДНА перечитка и ОДИН итог после неё (frontend-vite/CLAUDE.md).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCompanyOpTypes } from '@/components/operations/operationsApi';
import { api, ApiError } from '@/lib/api';
import { toastBulkResult } from '@/lib/toast';
import { scienceKeys } from '@/lib/science/api';
import { variantMarks, variantTreatment } from '@/lib/science/labels';
import type { TrialSummary } from '@/lib/science/types';
import { PickList } from './PickList';

function todayIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export interface OpResult {
  done: string[];
  failed: Array<{ name: string; reason: string }>;
}

/** Итог словами: «Записано по 2 вариантам; не удалось по Э — сезон заперт». */
export function opResultText(r: OpResult): { kind: 'success' | 'warning' | 'error'; text: string } {
  const ok = r.done.length;
  const bad = r.failed.map((f) => `${f.name} — ${f.reason}`).join('; ');
  if (!r.failed.length) return { kind: 'success', text: `Операция записана: ${r.done.join(', ')}` };
  if (!ok) return { kind: 'error', text: `Операция не записана: ${bad}` };
  return { kind: 'warning', text: `Записано: ${r.done.join(', ')}; не удалось: ${bad}` };
}

interface Props {
  trial: TrialSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TrialOperationDialog({ trial, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const opTypes = useCompanyOpTypes();
  const marks = variantMarks(trial.variants.map((v) => v.variant_role));
  const [typeId, setTypeId] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [date, setDate] = useState(todayIso());
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTypeId(null);
      setPicked([]);
      setDate(todayIso());
      setComment('');
      setError(null);
    }
  }, [open]);

  const typeItems = useMemo(
    () => (opTypes.data ?? []).map((t) => ({ value: t.id, label: t.local_name })),
    [opTypes.data],
  );

  async function save() {
    if (!typeId) return setError('Выберите тип операции');
    if (!picked.length) return setError('Отметьте хотя бы один вариант');
    const type = typeItems.find((t) => t.value === typeId);
    setBusy(true);
    setError(null);
    const result: OpResult = { done: [], failed: [] };
    for (const zoneId of picked) {
      const i = trial.variants.findIndex((v) => v.subzone_id === zoneId);
      const v = trial.variants[i];
      const name = `${marks[i]} · ${v.label}`;
      try {
        await api.post(`/api/v1/fields/${trial.field_id}/operations`, {
          field_id: trial.field_id,
          company_op_type_id: typeId,
          name: `${type?.label ?? 'Операция'} · ${v.label}`,
          subzone_id: zoneId,
          planned_start_date: date,
          actual_start_date: date,
          actual_end_date: date,
          lifecycle_state: 'done',
          comment: comment.trim() || variantTreatment(v.inputs, v.treatment_note),
        });
        result.done.push(name);
      } catch (e) {
        result.failed.push({ name, reason: e instanceof ApiError ? e.detail : 'ошибка сети' });
      }
    }
    await qc.invalidateQueries({ queryKey: scienceKeys.all });
    setBusy(false);
    const { kind, text } = opResultText(result);
    toastBulkResult(kind, text);
    if (!result.failed.length) onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить операцию</DialogTitle>
          <DialogDescription>
            По каждому отмеченному варианту запишется своя операция
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Тип операции</Label>
            <PickList
              aria-label="Тип операции"
              value={typeId}
              items={typeItems}
              loading={opTypes.isLoading}
              onChange={(v) => setTypeId(v)}
              placeholder="Опрыскивание, внесение…"
              searchPlaceholder="Название операции"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Варианты</Label>
            {trial.variants.map((v, i) => (
              <div key={v.subzone_id} className="flex items-start gap-2">
                <Checkbox
                  id={`op-v-${v.subzone_id}`}
                  checked={picked.includes(v.subzone_id)}
                  onCheckedChange={(c) =>
                    setPicked(
                      c === true
                        ? [...picked, v.subzone_id]
                        : picked.filter((x) => x !== v.subzone_id),
                    )
                  }
                />
                <Label htmlFor={`op-v-${v.subzone_id}`} className="font-normal leading-5">
                  {marks[i]} · {v.label}
                  <span className="block text-xs text-muted-foreground">
                    {variantTreatment(v.inputs, v.treatment_note)}
                  </span>
                </Label>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="op-date">Дата</Label>
              <Input
                id="op-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="op-comment">Что применяли</Label>
              <Input
                id="op-comment"
                value={comment}
                placeholder="из варианта"
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Записать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
